/**
 * @component ry-tree
 * @category  display
 * @summary   File-tree view (Archetype B — stateful collection). Authored as nested `<ry-tree-item label="..." open?>` markup; the wrapper transforms it into a CSS-only expand/collapse tree backed by hidden checkboxes. Items without children render as files (selectable); items with children render as folders (expandable). Selection fires `ry:select`, expansion fires `ry:toggle`. Add `[sortable]` to enable drag-and-drop rearrangement (fires `ry:move`).
 *
 * Reference: There is no Fomantic UI tree component — ry-tree is ry-ui-only.
 * The expand/collapse animation uses native `<input type="checkbox">` state
 * and `grid-template-rows` keyframes, so no JS is required for the open/close
 * visual; behavior layer is event emission + drag.
 *
 * @attribute sortable   boolean   default=false   Enables drag-and-drop rearrangement of items. Drag a folder or file onto another item to nest or reorder. Without this attribute, the tree is read-only.
 *
 * @property  value       TreeNode[]   Read-only — `toJSON()` shorthand. Returns the current tree shape as an array of `{ label, open?, children?, selected? }`.
 *
 * @method    toJSON()    Serialize the current tree state. Returns nested `TreeNode[]` with `label`, optional `open` (true for expanded folders), and `children` (recursive).
 *
 * @event ry:beforeselect
 *        detail: { label: string, previous: string | null }
 *        fires:  on file (leaf) click, before the previous `data-ry-selected` is cleared or the new one set. `label` is the item about to be selected, `previous` the currently selected label (null if none).
 *        cancelable: preventDefault() leaves the selection as it was; `ry:select` does not fire
 *
 * @event ry:select
 *        detail: { label: string }
 *        fires:  user clicks a file (leaf) item. `detail.label` is the clicked item's label. Only one item can be selected at a time; the previous selection's `data-ry-selected` is cleared.
 *
 * @event ry:beforetoggle
 *        detail: { label: string, open: boolean, previous: boolean }
 *        fires:  from the click on a folder's toggle checkbox (direct, or synthesized by its label / caret), before the expansion commits. `open` is the state about to be applied, `previous` the current one.
 *        cancelable: preventDefault() cancels the checkbox click — the platform reverts the toggle, the folder stays as it was, and `ry:toggle` does not fire
 *
 * @event ry:toggle
 *        detail: { label: string, open: boolean }
 *        fires:  user expands or collapses a folder (via clicking the toggle area or the disclosure caret). `detail.open` is the new state.
 *
 * @event ry:beforemove
 *        detail: { item: string, target: string, position: 'before' | 'after' | 'inside' }
 *        fires:  on drop (only when `[sortable]` is set), after the circular-drop guard, before the dragged item is re-parented in the DOM. Same detail as `ry:move`.
 *        cancelable: preventDefault() abandons the drop — the tree is unchanged and `ry:move` does not fire
 *
 * @event ry:move
 *        detail: { item: string, target: string, position: 'before' | 'after' | 'inside' }
 *        fires:  after a successful drag-and-drop rearrangement (only when `[sortable]` is set). `position` indicates where the dragged item landed relative to the target.
 *
 * @keyboard  None at the wrapper level — folders use the native `<input type="checkbox">` for keyboard expand/collapse (Space). Selection is mouse-only; consumers wire keyboard selection externally.
 *
 * @state     Per-folder checkbox state controls expansion. Per-item `data-ry-selected` attribute marks the active selection (one at a time).
 *
 * @content   `<ry-tree-item label="..." open?>` children, nestable. The wrapper transforms them at setup into the rendered tree markup; raw `<ry-tree-item>` tags no longer exist in DOM post-mount.
 *
 * @dom
 *   ry-tree[draggable?]
 *     > ul.ry-tree__root
 *       > li.ry-tree__item
 *         > input.ry-tree__toggle  type="checkbox"  (only when folder has children)
 *         > div.ry-tree__row       [data-ry-target="folder"|"file"]   data-ry-label
 *           > caret + icon + label
 *         > ul.ry-tree__children   (only for folders)
 *           > li.ry-tree__item ... (recursive)
 *
 * @gotcha  `<ry-tree-item>` is NOT a live custom element — it's a markup convention parsed at setup. After mount, the original `<ry-tree-item>` tags are replaced. Don't query for them at runtime; query `[data-ry-target="file"]` / `[data-ry-target="folder"]` instead.
 *
 * @gotcha  Selection fires on FILE items only (leaves). Clicking a folder row toggles its expansion via the checkbox but does NOT emit `ry:select`. To select a folder, treat it as a file (no children).
 *
 * @gotcha  `toJSON()` reflects the LIVE state — including in-progress drag re-arrangements. Call it before / after drag to see the change. The serialized `open` flag tracks the user's expansion state, not just initial markup.
 *
 * @gotcha  Drag-and-drop interactions are not modelled by happy-dom — `[sortable]` works in real browsers but cannot be exercised in unit tests with the current harness. Cover drag-drop with Playwright / E2E if needed; vitest covers the rest.
 *
 * @gotcha  Multiple items with the same `label` are valid but `ry:select` events become ambiguous (consumers can't distinguish them via the event detail alone). Use a separate identifier in the DOM if labels collide.
 *
 * @example basic
 * ```html
 * <ry-tree>
 *   <ry-tree-item label="src" open>
 *     <ry-tree-item label="app" open>
 *       <ry-tree-item label="layout.tsx"></ry-tree-item>
 *       <ry-tree-item label="page.tsx"></ry-tree-item>
 *     </ry-tree-item>
 *     <ry-tree-item label="components">
 *       <ry-tree-item label="Button.tsx"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree-item>
 * </ry-tree>
 * ```
 *
 * @example sortable
 * ```html
 * <ry-tree sortable>
 *   <ry-tree-item label="src" open>
 *     <ry-tree-item label="app" open>
 *       <ry-tree-item label="layout.tsx"></ry-tree-item>
 *       <ry-tree-item label="page.tsx"></ry-tree-item>
 *     </ry-tree-item>
 *     <ry-tree-item label="components" open>
 *       <ry-tree-item label="ui" open>
 *         <ry-tree-item label="button.tsx" selected></ry-tree-item>
 *       </ry-tree-item>
 *       <ry-tree-item label="header.tsx"></ry-tree-item>
 *       <ry-tree-item label="footer.tsx"></ry-tree-item>
 *     </ry-tree-item>
 *     <ry-tree-item label="lib" open>
 *       <ry-tree-item label="utils.ts"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree-item>
 * </ry-tree>
 * ```
 *
 * @tutorial mount-transforms-to-rendered-tree
 * ```js
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="root" open>
 *       <ry-tree-item label="leaf"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * // <ry-tree-item> tags are replaced by the rendered list
 * await expectEqual(el.querySelector('ry-tree-item'), null, 'authored tags consumed');
 * await expectEqual(!!el.querySelector('.ry-tree__root'), true, 'root list rendered');
 * await expectEqual(el.querySelectorAll('.ry-tree__item').length, 2, 'two items');
 * ```
 *
 * @tutorial folder-and-file-roles
 * ```js
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="folder" open>
 *       <ry-tree-item label="file.txt"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const folders = el.querySelectorAll('[data-ry-target="folder"]');
 * const files = el.querySelectorAll('[data-ry-target="file"]');
 * await expectEqual(folders.length, 1);
 * await expectEqual(files.length, 1);
 * await expectEqual(folders[0].dataset.ryLabel, 'folder');
 * await expectEqual(files[0].dataset.ryLabel, 'file.txt');
 * ```
 *
 * @tutorial click-file-fires-ry-select
 * ```js
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="folder" open>
 *       <ry-tree-item label="a.txt"></ry-tree-item>
 *       <ry-tree-item label="b.txt"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const fired = [];
 * el.addEventListener('ry:select', (e) => fired.push(e.detail.label));
 * const files = el.querySelectorAll('[data-ry-target="file"]');
 * files[0].click();
 * await expectEqual(fired[0], 'a.txt');
 * files[1].click();
 * await expectEqual(fired[1], 'b.txt');
 * await expectEqual(fired.length, 2);
 * ```
 *
 * @tutorial only-one-file-selected-at-a-time
 * ```js
 * // Wrong assumption: ry-tree allows multi-select. It does NOT — selection
 * // is single (clicking a new file clears the previous selection mark).
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="folder" open>
 *       <ry-tree-item label="a.txt"></ry-tree-item>
 *       <ry-tree-item label="b.txt"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const files = el.querySelectorAll('[data-ry-target="file"]');
 * files[0].click();
 * files[1].click();
 * await expectEqual(files[0].hasAttribute('data-ry-selected'), false);
 * await expectAttr(files[1], 'data-ry-selected', '');
 * await expectEqual(el.querySelectorAll('[data-ry-selected]').length, 1);
 * ```
 *
 * @tutorial folder-toggle-fires-ry-toggle
 * ```js
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="folder">
 *       <ry-tree-item label="leaf"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const fired = [];
 * el.addEventListener('ry:toggle', (e) => fired.push(e.detail));
 * const toggle = el.querySelector('.ry-tree__toggle');
 * toggle.checked = true;
 * toggle.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].label, 'folder');
 * await expectEqual(fired[0].open, true);
 * toggle.checked = false;
 * toggle.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(fired[1].open, false);
 * ```
 *
 * @tutorial beforeselect-veto
 * ```js
 * // preventDefault() on ry:beforeselect leaves the selection as it was.
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="folder" open>
 *       <ry-tree-item label="a.txt" selected></ry-tree-item>
 *       <ry-tree-item label="b.txt"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const files = el.querySelectorAll('[data-ry-target="file"]');
 * const selected = [];
 * el.addEventListener('ry:select', (e) => selected.push(e.detail.label));
 * let seen;
 * el.addEventListener('ry:beforeselect', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * files[1].click();
 * await expectEqual(seen.label, 'b.txt', 'detail.label is the item about to be selected');
 * await expectEqual(seen.previous, 'a.txt', 'detail.previous is the current selection');
 * await expectAttr(files[0], 'data-ry-selected', '', 'vetoed select keeps a.txt selected');
 * await expectEqual(files[1].hasAttribute('data-ry-selected'), false);
 * await expectEqual(selected.length, 0, 'ry:select did not fire');
 * files[1].click();
 * await expectAttr(files[1], 'data-ry-selected', '', 'without the veto, the click selects');
 * await expectEqual(selected.length, 1, 'ry:select fired once');
 * ```
 *
 * @tutorial beforetoggle-veto
 * ```js
 * // preventDefault() on ry:beforetoggle cancels the checkbox click, so the
 * // folder stays collapsed and ry:toggle never fires.
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="folder">
 *       <ry-tree-item label="leaf"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const toggle = el.querySelector('.ry-tree__toggle');
 * const toggled = [];
 * el.addEventListener('ry:toggle', () => toggled.push(1));
 * let seen;
 * el.addEventListener('ry:beforetoggle', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * toggle.click();
 * await expectEqual(seen.open, true, 'detail.open is the state about to be applied');
 * await expectEqual(seen.previous, false, 'detail.previous is the current state');
 * await expectEqual(toggle.checked, false, 'vetoed toggle leaves the folder collapsed');
 * await expectEqual(toggled.length, 0, 'ry:toggle did not fire');
 * toggle.click();
 * await expectEqual(toggle.checked, true, 'without the veto, the click expands');
 * await expectEqual(toggled.length, 1, 'ry:toggle fired once');
 * ```
 *
 * @tutorial click-folder-row-does-not-fire-ry-select
 * ```js
 * // Wrong assumption: clicking a folder ROW (the label area) fires ry:select.
 * // Only file items emit selection; folder rows toggle expansion.
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="folder">
 *       <ry-tree-item label="leaf"></ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const fired = [];
 * el.addEventListener('ry:select', () => fired.push(1));
 * const folder = el.querySelector('[data-ry-target="folder"]');
 * folder.click();
 * await expectEqual(fired.length, 0, 'folder click does not fire ry:select');
 * ```
 *
 * @tutorial toJSON-reflects-current-tree
 * ```js
 * const el = mount(`
 *   <ry-tree>
 *     <ry-tree-item label="src" open>
 *       <ry-tree-item label="app">
 *         <ry-tree-item label="layout.tsx"></ry-tree-item>
 *       </ry-tree-item>
 *     </ry-tree-item>
 *   </ry-tree>
 * `);
 * const data = el.toJSON();
 * await expectEqual(data.length, 1);
 * await expectEqual(data[0].label, 'src');
 * await expectEqual(data[0].open, true);
 * await expectEqual(data[0].children.length, 1);
 * await expectEqual(data[0].children[0].label, 'app');
 * await expectEqual(data[0].children[0].children[0].label, 'layout.tsx');
 * ```
 *
 * @tutorial value-getter-aliases-toJSON
 * ```js
 * const el = mount('<ry-tree><ry-tree-item label="x"></ry-tree-item></ry-tree>');
 * const data = el.toJSON();
 * await expectEqual(JSON.stringify(el.value), JSON.stringify(data));
 * ```
 *
 * @see ry-dropdown
 * @see ry-accordion
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

let treeIdCounter = 0;

const DRAG_THRESHOLD = 5;

function icon16(name: string): string {
  return getIcon(name)
    .replace(/width="24"/g, 'width="16"')
    .replace(/height="24"/g, 'height="16"')
    .replace(/viewBox/, 'class="ry-tree__icon" viewBox');
}

export interface TreeNode {
  label: string;
  children?: TreeNode[];
  open?: boolean;
  selected?: boolean;
}

export class RyTree extends RyElement {
  // Drag state
  #dragPending = false;
  #dragActive = false;
  #dragOriginX = 0;
  #dragOriginY = 0;
  #draggedItem: HTMLElement | null = null;
  #ghost: HTMLElement | null = null;
  #dropTarget: HTMLElement | null = null;
  #dropPosition: 'before' | 'after' | 'inside' | null = null;

  setup(): void {
    this.#build();

    // Delegate click for file selection
    this.on(this, 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      const fileItem = target.closest<HTMLElement>('[data-ry-target="file"]');
      if (!fileItem) return;

      const label = fileItem.dataset.ryLabel ?? '';
      const previous = this.$<HTMLElement>('[data-ry-selected]')?.dataset.ryLabel ?? null;
      if (!this.emit('beforeselect', { label, previous })) return;

      this.$$('[data-ry-selected]').forEach(el => el.removeAttribute('data-ry-selected'));
      fileItem.setAttribute('data-ry-selected', '');

      this.emit('select', { label });
    });

    // Interception for folder expand/collapse. By the time the checkbox's click
    // dispatches its `checked` already reflects the NEW value; preventDefault()
    // makes the platform revert it and suppress `change` (so ry:toggle stays silent).
    this.on(this, 'click', (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (!input.classList?.contains('ry-tree__toggle')) return;
      const label = this.#toggleLabel(input);
      if (!this.emit('beforetoggle', { label, open: input.checked, previous: !input.checked })) {
        e.preventDefault();
      }
    });

    // Emit toggle events when folders open/close
    this.on(this, 'change', (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (!input.classList.contains('ry-tree__toggle')) return;

      this.emit('toggle', {
        label: this.#toggleLabel(input),
        open: input.checked,
      });
    });

    // Drag and drop — only when draggable attribute is present
    if (this.hasAttribute('sortable')) {
      this.#bindDrag();
    }
  }

  #toggleLabel(input: HTMLElement): string {
    const li = input.closest<HTMLElement>('.ry-tree__item');
    return li?.querySelector<HTMLElement>('[data-ry-target="folder"]')?.dataset.ryLabel ?? '';
  }

  // ── Build ──────────────────────────────────────────────────

  #build(): void {
    if (this.$('.ry-tree__root')) return;

    const items = Array.from(this.children) as HTMLElement[];
    const ul = document.createElement('ul');
    ul.className = 'ry-tree__root';

    for (const item of items) {
      ul.appendChild(this.#buildItem(item));
    }

    this.innerHTML = '';
    this.appendChild(ul);
  }

  #buildItem(source: HTMLElement): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'ry-tree__item';

    const label = source.getAttribute('label') ?? '';
    const isOpen = source.hasAttribute('open');
    const isSelected = source.hasAttribute('selected');
    const children = Array.from(source.children).filter(
      c => c.tagName === 'RY-TREE-ITEM'
    ) as HTMLElement[];
    const isFolder = children.length > 0;

    if (isFolder) {
      const id = `ry-tree-${++treeIdCounter}`;

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = id;
      checkbox.className = 'ry-tree__toggle';
      if (isOpen) checkbox.checked = true;

      const labelEl = document.createElement('label');
      labelEl.htmlFor = id;
      labelEl.className = 'ry-tree__label';
      labelEl.setAttribute('data-ry-target', 'folder');
      labelEl.dataset.ryLabel = label;
      labelEl.innerHTML = `
        <span class="ry-tree__icon-closed">${icon16('folder')}</span>
        <span class="ry-tree__icon-open">${icon16('folder-open')}</span>
        ${label}
      `;

      const wrapper = document.createElement('div');
      wrapper.className = 'ry-tree__children-wrapper';

      const childUl = document.createElement('ul');
      childUl.className = 'ry-tree__children';

      for (const child of children) {
        childUl.appendChild(this.#buildItem(child));
      }

      wrapper.appendChild(childUl);
      li.appendChild(checkbox);
      li.appendChild(labelEl);
      li.appendChild(wrapper);
    } else {
      const fileEl = document.createElement('div');
      fileEl.className = 'ry-tree__file';
      fileEl.setAttribute('data-ry-target', 'file');
      fileEl.dataset.ryLabel = label;
      if (isSelected) fileEl.setAttribute('data-ry-selected', '');
      fileEl.innerHTML = `${icon16('file')} ${label}`;

      li.appendChild(fileEl);
    }

    return li;
  }

  // ── Serialization ──────────────────────────────────────────

  /** Serialize the current tree state to JSON. */
  toJSON(): TreeNode[] {
    const root = this.$<HTMLElement>('.ry-tree__root');
    if (!root) return [];
    return this.#serializeChildren(root);
  }

  /** Alias for toJSON(). */
  get value(): TreeNode[] {
    return this.toJSON();
  }

  #serializeChildren(container: HTMLElement): TreeNode[] {
    const items = Array.from(container.children).filter(
      (c): c is HTMLElement => c instanceof HTMLElement && c.classList.contains('ry-tree__item')
    );
    return items.map(item => this.#serializeItem(item));
  }

  #serializeItem(item: HTMLElement): TreeNode {
    const folderLabel = item.querySelector<HTMLElement>(':scope > [data-ry-target="folder"]');
    const fileDiv = item.querySelector<HTMLElement>(':scope > [data-ry-target="file"]');

    if (folderLabel) {
      const toggle = item.querySelector<HTMLInputElement>(':scope > .ry-tree__toggle');
      const childrenList = item.querySelector<HTMLElement>('.ry-tree__children');
      const node: TreeNode = { label: folderLabel.dataset.ryLabel ?? '' };
      if (toggle?.checked) node.open = true;
      if (childrenList) {
        const children = this.#serializeChildren(childrenList);
        if (children.length > 0) node.children = children;
      }
      return node;
    }

    // File
    const node: TreeNode = { label: fileDiv?.dataset.ryLabel ?? '' };
    if (fileDiv?.hasAttribute('data-ry-selected')) node.selected = true;
    return node;
  }

  /** Create a tree element from JSON data. */
  static from(data: TreeNode[]): RyTree {
    const tree = document.createElement('ry-tree') as RyTree;
    for (const node of data) {
      tree.appendChild(RyTree.#nodeToElement(node));
    }
    return tree;
  }

  static #nodeToElement(node: TreeNode): HTMLElement {
    const item = document.createElement('ry-tree-item');
    item.setAttribute('label', node.label);
    if (node.open) item.setAttribute('open', '');
    if (node.selected) item.setAttribute('selected', '');
    if (node.children) {
      for (const child of node.children) {
        item.appendChild(RyTree.#nodeToElement(child));
      }
    }
    return item;
  }

  // ── Drag and Drop ──────────────────────────────────────────

  #bindDrag(): void {
    this.on(this, 'pointerdown', this.#onPointerDown);
    this.on(document, 'pointermove', this.#onPointerMove);
    this.on(document, 'pointerup', this.#onPointerUp);
  }

  #onPointerDown = (e: PointerEvent): void => {
    const target = e.target as HTMLElement;
    const handle = target.closest<HTMLElement>('.ry-tree__label, .ry-tree__file');
    if (!handle) return;

    const item = handle.closest<HTMLElement>('.ry-tree__item');
    if (!item) return;

    // Record origin — don't start drag yet (distance threshold)
    this.#dragPending = true;
    this.#dragOriginX = e.clientX;
    this.#dragOriginY = e.clientY;
    this.#draggedItem = item;
  };

  #onPointerMove = (e: PointerEvent): void => {
    if (!this.#dragPending && !this.#dragActive) return;

    // Check distance threshold before activating
    if (this.#dragPending && !this.#dragActive) {
      const dx = e.clientX - this.#dragOriginX;
      const dy = e.clientY - this.#dragOriginY;
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

      // Threshold crossed — activate drag
      this.#dragPending = false;
      this.#dragActive = true;
      this.#activateDrag(e);
    }

    if (!this.#dragActive || !this.#ghost) return;

    // Update ghost position
    this.#ghost.style.left = `${e.clientX + 12}px`;
    this.#ghost.style.top = `${e.clientY - 14}px`;

    // Hide ghost momentarily so elementFromPoint sees through it
    this.#ghost.style.display = 'none';
    const elUnder = document.elementFromPoint(e.clientX, e.clientY);
    this.#ghost.style.display = '';

    if (!elUnder) {
      this.#clearIndicator();
      return;
    }

    const targetHandle = elUnder.closest<HTMLElement>('.ry-tree__label, .ry-tree__file');
    if (!targetHandle) {
      this.#clearIndicator();
      return;
    }

    const targetItem = targetHandle.closest<HTMLElement>('.ry-tree__item');
    if (!targetItem || targetItem === this.#draggedItem) {
      this.#clearIndicator();
      return;
    }

    // Don't allow drops onto another <ry-tree>. elementFromPoint walks the whole
    // document, so without this guard, dragging in tree A can target tree B and
    // re-parent items across hosts, corrupting state on both sides.
    if (!this.contains(targetItem)) {
      this.#clearIndicator();
      return;
    }

    // Don't allow dropping into own descendants
    if (this.#draggedItem?.contains(targetItem)) {
      this.#clearIndicator();
      return;
    }

    // Determine position: top third / middle third / bottom third
    const rect = targetHandle.getBoundingClientRect();
    const relY = e.clientY - rect.top;
    const h = rect.height;
    const isFolder = targetItem.querySelector(':scope > .ry-tree__toggle') !== null;

    let position: 'before' | 'after' | 'inside';
    if (isFolder && relY > h * 0.25 && relY < h * 0.75) {
      position = 'inside';
    } else if (relY < h * 0.5) {
      position = 'before';
    } else {
      position = 'after';
    }

    this.#setIndicator(targetItem, position);
  };

  #onPointerUp = (): void => {
    if (this.#dragActive) {
      this.#finishDrag();
    }
    this.#dragPending = false;
    this.#dragActive = false;
    this.#draggedItem = null;
  };

  #activateDrag(e: PointerEvent): void {
    if (!this.#draggedItem) return;

    // Prevent the click from toggling the folder checkbox
    e.preventDefault();

    // Mark as dragging and collapse to thin placeholder so parent targets are reachable
    this.#draggedItem.setAttribute('data-ry-dragging', '');
    this.#draggedItem.classList.add('ry-tree__item--collapsed');

    // Create ghost
    const handle = this.#draggedItem.querySelector<HTMLElement>('.ry-tree__label, .ry-tree__file');
    if (!handle) return;

    const ghost = handle.cloneNode(true) as HTMLElement;
    ghost.className = 'ry-tree__ghost';
    // Copy label/file styles for size
    ghost.style.display = 'flex';
    ghost.style.alignItems = 'center';
    ghost.style.gap = 'var(--ry-space-2, 0.5rem)';
    ghost.style.padding = 'var(--ry-space-1, 0.25rem) var(--ry-space-2, 0.5rem)';
    ghost.style.height = '28px';
    ghost.style.fontSize = 'var(--ry-text-sm, 0.875rem)';
    ghost.style.whiteSpace = 'nowrap';
    ghost.style.left = `${e.clientX + 12}px`;
    ghost.style.top = `${e.clientY - 14}px`;
    document.body.appendChild(ghost);
    this.#ghost = ghost;
  }

  #finishDrag(): void {
    // Remove ghost
    if (this.#ghost?.parentNode) {
      this.#ghost.parentNode.removeChild(this.#ghost);
    }
    this.#ghost = null;

    // Clear visual
    this.#draggedItem?.classList.remove('ry-tree__item--collapsed');
    this.#draggedItem?.removeAttribute('data-ry-dragging');
    const dropTarget = this.#dropTarget;
    const dropPosition = this.#dropPosition;
    this.#clearIndicator();

    // Perform DOM move
    if (this.#draggedItem && dropTarget && dropPosition) {
      this.#performMove(this.#draggedItem, dropTarget, dropPosition);
    }
  }

  #setIndicator(item: HTMLElement, position: 'before' | 'after' | 'inside'): void {
    if (this.#dropTarget === item && this.#dropPosition === position) return;
    this.#clearIndicator();
    item.setAttribute('data-ry-drop-target', position);
    this.#dropTarget = item;
    this.#dropPosition = position;
  }

  #clearIndicator(): void {
    this.#dropTarget?.removeAttribute('data-ry-drop-target');
    this.#dropTarget = null;
    this.#dropPosition = null;
  }

  #performMove(
    draggedItem: HTMLElement,
    dropTarget: HTMLElement,
    position: 'before' | 'after' | 'inside'
  ): void {
    // Safety: can't drop inside own descendant (circular)
    if (position === 'inside' && dropTarget.contains(draggedItem)) return;

    const detail = {
      item: this.#itemLabel(draggedItem),
      target: this.#itemLabel(dropTarget),
      position,
    };
    if (!this.emit('beforemove', detail)) return;

    if (position === 'before') {
      dropTarget.parentNode?.insertBefore(draggedItem, dropTarget);
    } else if (position === 'after') {
      dropTarget.parentNode?.insertBefore(draggedItem, dropTarget.nextSibling);
    } else {
      // Inside — append to folder's children list
      const childrenList = dropTarget.querySelector<HTMLElement>('.ry-tree__children');
      if (childrenList) {
        childrenList.appendChild(draggedItem);
        // Auto-open the folder
        const toggle = dropTarget.querySelector<HTMLInputElement>(':scope > .ry-tree__toggle');
        if (toggle && !toggle.checked) {
          toggle.checked = true;
        }
      }
    }

    this.emit('move', detail);
  }

  #itemLabel(item: HTMLElement): string {
    const folder = item.querySelector<HTMLElement>(':scope > [data-ry-target="folder"]');
    const file = item.querySelector<HTMLElement>(':scope > [data-ry-target="file"]');
    return folder?.dataset.ryLabel ?? file?.dataset.ryLabel ?? '';
  }
}

export class RyTreeItem extends RyElement {
  // Declarative container consumed by RyTree#build
}

customElements.define('ry-tree', RyTree);
customElements.define('ry-tree-item', RyTreeItem);
